<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <div>
    <figure>
      <div class="thumbnail-wrapper">
        <div v-show="!isImageLoaded" class="skeleton isWaiting" />
        <!-- eslint-disable-next-line vue/no-template-shadow -->
        <router-link :to="videoPath">
          <!-- a free video, signed out: free, but sign-in first (2026-10-14) -->
          <span v-if="isFreeForSignIn" class="freeTag">Free with sign-up</span>
          <img
            class="thumb"
            :src="smallThumbnail(options.showcases, showcase, vimeoHash)"
            :srcset="thumbnailSrcset(options.showcases, showcase, vimeoHash)"
            sizes="(max-width: 579px) 50vw, 33vw"
            :class="{loaded: isImageLoaded}"
            loading="lazy"
            @load="isImageLoaded = true" />
        </router-link>
      </div>
      <figcaption>
        <div class="name">
          <span
            v-if="options.get_privacy(vimeoHash, showcase)"
            class="lock"
            :class="{isUnlocked: hasVideoAccess(showcase)}"
            role="img"
            :aria-label="hasVideoAccess(showcase) ? 'Unlocked' : 'Locked'" />
          <span v-if="options.get_privacy(vimeoHash, showcase)">&nbsp;</span>
          <router-link :to="videoPath">
            {{ title }}
          </router-link>
          <Slides :name="fullName" :year="year" />
        </div>
      </figcaption>
    </figure>
    <span class="videoTags">
      <Tag :label="showcase.match(/\d+/)[0]" :selected="options.year" @update="filterByYear" />
      <Tag
        :label="options.showcases[showcase]['videos'][vimeoHash].tag"
        :selected="options.session"
        @update="filterBySession" />
    </span>
  </div>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(`VideoGalleryItem: ${msg}`); // eslint-disable-line
// eslint-disable-next-line
const props = defineProps(["options", "showcase", "vimeoHash"]);
import {computed, ref} from "vue";
const isImageLoaded = ref(false);
import Slides from "@/components/People/Person/Slides.vue";
import Tag from "@/components/Tag";
import useUserSafe from "@/composables/useUserSafe";
import {
  getHasVideoAccess, getPictures, getSmallThumbnail, getThumbnailUrl, getVideoStatus
} from "@/components/Video/videoCards";
import {getVideoPath} from "@/components/Video/videoPaths";
const emit = defineEmits(["filterByYear", "filterBySession"]);
const nameRegex = /^(.*):/;
const {isSignedIn, user} = useUserSafe();
const video = computed(() => props.options.showcases[props.showcase]["videos"][props.vimeoHash]);
const title = computed(() => video.value.name);
// the video's address by its speakers' names (videoPaths.js), as the link shows on hover, not
// its hash (Seán, 2026-11-05); a film's card, its bird off, opens the preview's (Video/films.js)
const videoPath = computed(() => {
  const hash = video.value.linkTo || props.vimeoHash;
  return `/Videos/${getVideoPath(props.options.showcases, hash) || hash}`;
});
const fullName = computed(() => {
  let nameMatch = title.value.match(nameRegex);
  if (!nameMatch) return;
  nameMatch = nameMatch[1];
  log(`nameMatch = ${nameMatch}`);
  return nameMatch;
});
const year = computed(() => props.showcase.match(/\d+/)[0]);
function hasVideoAccess(showcase) {
  const viewer = {user: user.value, isSignedIn: isSignedIn.value};
  return getHasVideoAccess({video: video.value, hash: props.vimeoHash, showcase, ...viewer});
}
// a Free video (claude/VIDEOS.md) needs sign-in: signed out, it says so on the thumbnail (a
// Public one, e.g. a showreel, plays for anyone: no tag)
const isFreeVideo = computed(() => getVideoStatus(video.value) === "free");
const isFreeForSignIn = computed(() => isFreeVideo.value && !isSignedIn.value);
function smallThumbnail(filteredShowcases, showcase, vimeoHash) {
  return getSmallThumbnail(filteredShowcases, showcase, vimeoHash);
}
function thumbnailSrcset(filteredShowcases, showcase, vimeoHash) {
  const pictures = getPictures(filteredShowcases, showcase, vimeoHash);
  if (!pictures) return "";
  return pictures.sizes
    .map((sizeStr) => ({sizeStr, width: parseInt(sizeStr)}))
    .filter(({width}) => width >= 295 && width <= 1280 && !isNaN(width))
    .map(({sizeStr, width}) => `${getThumbnailUrl(pictures, sizeStr)} ${width}w`)
    .join(", ");
}
function filterByYear(year) {
  emit("filterByYear", year);
}
function filterBySession(session) {
  emit("filterBySession", session);
}
</script>

<style scoped>
div a {
  color: var(--text-color);
}
div a:hover {
  color: var(--link-color-subtle);
}
figure {
  cursor: pointer;
  /*display: inline-table; */
  /* https://stackoverflow.com/q/30686191 */
  margin: 0px;
  padding: 0px;
  /* ideally, automatically switch to hi-res thumbnails on large screens */
  max-width: 100%; /* https://css-tricks.com/preventing-a-grid-blowout/ */
  flex: 1;
}
.thumbnail-wrapper {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
}
.thumbnail-wrapper a {
  display: block;
}
/* the thumbnail's place until it loads: global.css' waiting sweep, in the page's dark greys */
.skeleton {
  position: absolute;
  inset: 0;
  --waiting-base: #484848;
  --waiting-sweep: #585858;
}
figure img.thumb {
  width: 100%;
  opacity: 0;
  transition: opacity 0.15s ease;
}
figure img.thumb.loaded {
  opacity: 1;
}
figure img.thumb:hover {
  filter: brightness(105%);
}
/* on the thumbnail's lower left: dark, so it reads on any image */
.freeTag {
  position: absolute;
  left: 0.4rem;
  bottom: 0.4rem;
  z-index: 1;
  padding: 0.15rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--orange-on-dark); /* the access buttons' orange (VideoAccessActionButton.vue) */
  background: rgba(0, 0, 0, 0.72);
  border-radius: 999px;
  pointer-events: none;
}
/* the year and session tags: Tag.vue's own look (the filter controls' colours, green chosen) */
/* the padlock: about the title's capital height; locked in orange (needs buying), unlocked in the
   tags' grey (Tag.vue #a7a7a7: quiet metadata). The unlocked PNG is a shape filled with that grey
   (a mask), so its colour isn't baked into the image */
.lock {
  display: inline-block;
  height: 0.7em;
  vertical-align: baseline;
  background: url("~@/assets/img/lock-orange.png") center / contain no-repeat;
  aspect-ratio: 25 / 32;
}
.lock.isUnlocked {
  aspect-ratio: 25 / 35;
  background: #a7a7a7;
  mask: url("~@/assets/img/unlocked.png") center / contain no-repeat;
  -webkit-mask: url("~@/assets/img/unlocked.png") center / contain no-repeat;
}

/* figcaption {
  background-color: red;
} */

button {
  font-size: 9.9px;
}
button + button {
  margin-left: 0.5rem; /* space between individual video tags */
}
</style>
